import React from 'react';
import { Plus, Search, X } from 'lucide-react';
import { DMA_REGIONS, type DMARegion } from '../../../data/dmaList';

interface DMASearchSelectorProps {
  selectedDMAs: string[];
  onChange: (next: string[]) => void;
  accentColor: string;
  // DMAs the publisher identified at onboarding (their station's coverage).
  // Surfaced as one-click "Suggested" chips so a local-station advertiser
  // doesn't have to scroll the full Nielsen list. Optional — when omitted
  // or empty, the suggested-row simply hides.
  suggestedDMACodes?: string[];
}

// Search-and-pick over the full DMA list. Mirrors the mockup-proposals
// DMASearchSelector but reworked for the selfservice wizard's slate-on-white
// palette and tightened to drop the proposal-flow-specific "select all"
// affordance (it doesn't make sense for an advertiser picking target DMAs).
const DMASearchSelector: React.FC<DMASearchSelectorProps> = ({
  selectedDMAs,
  onChange,
  accentColor,
  suggestedDMACodes,
}) => {
  const [search, setSearch] = React.useState('');
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const filtered = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return DMA_REGIONS;
    return DMA_REGIONS.filter(
      (d) => d.name.toLowerCase().includes(term) || d.code.toLowerCase().includes(term),
    );
  }, [search]);

  const selectedRegions = React.useMemo<DMARegion[]>(
    () => selectedDMAs
      .map((id) => DMA_REGIONS.find((d) => d.id === id))
      .filter((d): d is DMARegion => Boolean(d)),
    [selectedDMAs],
  );

  const add = (id: string) => {
    if (selectedDMAs.includes(id)) return;
    onChange([...selectedDMAs, id]);
    setSearch('');
    setDropdownOpen(false);
  };

  const remove = (id: string) => {
    onChange(selectedDMAs.filter((d) => d !== id));
  };

  const suggestions = React.useMemo<DMARegion[]>(() => {
    if (!suggestedDMACodes || suggestedDMACodes.length === 0) return [];
    return suggestedDMACodes
      .map((code) => DMA_REGIONS.find((d) => d.code === code))
      .filter((d): d is DMARegion => Boolean(d) && !selectedDMAs.includes((d as DMARegion).id));
  }, [suggestedDMACodes, selectedDMAs]);

  return (
    <div className="space-y-3">
      <div className="relative" ref={dropdownRef}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          aria-label="Search DMAs"
          placeholder="Search DMAs by name or code…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setDropdownOpen(true);
          }}
          onFocus={() => setDropdownOpen(true)}
          className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-1"
          style={{ ['--tw-ring-color' as string]: accentColor }}
        />
        {dropdownOpen && (
          <div
            role="listbox"
            aria-label="DMA search results"
            className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            {filtered.length > 0 ? (
              filtered.map((dma) => {
                const isSelected = selectedDMAs.includes(dma.id);
                return (
                  <button
                    key={dma.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => add(dma.id)}
                    disabled={isSelected}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                      isSelected ? 'text-slate-400 cursor-not-allowed' : 'text-slate-900'
                    }`}
                  >
                    <span className="font-medium">{dma.code}</span>
                    <span className="text-slate-500"> — {dma.name}</span>
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-2 text-sm text-slate-500">No DMAs found</div>
            )}
          </div>
        )}
      </div>

      {suggestions.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
            Suggested DMAs
          </div>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((dma) => (
              <button
                key={dma.id}
                type="button"
                onClick={() => add(dma.id)}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-slate-50 text-slate-700 hover:bg-slate-100 rounded-full border border-slate-200"
              >
                <Plus className="w-3 h-3" />
                {dma.code} — {dma.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedRegions.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">
              {selectedRegions.length} selected
            </span>
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-[12px] text-slate-500 hover:text-red-600 underline"
            >
              Clear all
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {selectedRegions.map((dma) => (
              <span
                key={dma.id}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-slate-100 text-slate-800 rounded-full"
              >
                {dma.code} — {dma.name}
                <button
                  type="button"
                  onClick={() => remove(dma.id)}
                  aria-label={`Remove ${dma.code}`}
                  className="text-slate-500 hover:text-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DMASearchSelector;
